import { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import ReactAudioPlayer from "react-audio-player";
import axios from "axios";
import TakeActionButton from "../TakeAction";
import { changeCall_AuditStatus } from "../../../../API/agentApis";
import useAuthStore from "../../../../store/authStore";
import { teleCollectioAsgmt } from "../data";
import { Alert } from "antd";
import { FaCheckCircle, FaInfo, FaThumbsDown, FaThumbsUp, FaTimesCircle } from "react-icons/fa";
import useGeneralStore from "../../../../store/generalStore";
import { showToast } from "../../../../utils/handleToast";

const Poonawalla = () => {
  const [auditStatus, setAuditStatus] = useState({}); // Tracks API-verified status for each loan_account_no
  const [showPopup, setShowPopup] = useState(false);
  const [submittedItems, setSubmittedItems] = useState({});
  const [fullyPlayedRecordings, setFullyPlayedRecordings] = useState({});
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [audioCurrentTimes, setAudioCurrentTimes] = useState({});
  const audioRefs = useRef({});
  const userData = useAuthStore((state) => state.userData);
  const setCallAudited = useAuthStore((state) => state.setCallAudited);
  const history = useHistory();
  const setAuditCallStepCount = useGeneralStore((state) => state.setAuditCallStepCount);

  // Get only first 10 Poonawalla records
  const poonawallaData = teleCollectioAsgmt
    .filter((item) => item.client_name === "Poonawalla")
    .slice(0, 10); // Take only first 10 records

  // Fetch audit status for first 10 Poonawalla loan_account_no on mount
  useEffect(() => {
    const fetchAuditStatus = async () => {
      const statusMap = {};

      try {
        const auditPromises = poonawallaData.map(async (item) => {
          const loanAccountNo = item.loan_account_no;
          const response = await axios.get(
            `https://api.themedius.ai/call/api/agent_call_audit_assignment/?loan_account_no=${loanAccountNo}`,
            { headers: { "Content-Type": "application/json" } }
          );
          // Check if the loan_account_no matches the response
          const isVerified = response.data[0]?.loan_account_no === loanAccountNo;
          statusMap[loanAccountNo] = isVerified;
        });

        await Promise.all(auditPromises);
        setAuditStatus(statusMap);
      } catch (error) {
        console.error("Error fetching audit status:", error);
      }
    };

    if (poonawallaData.length > 0) {
      fetchAuditStatus();
    }
  }, [userData]);

  // Handle individual item submission (optional, kept for compatibility)
  const handleSubmission = (itemId) => {
    setSubmittedItems((prev) => ({
      ...prev,
      [itemId]: true,
    }));
  };

  // Handle audio play start
  const handlePlayStart = (loanAccountNo) => {
    // Pause other playing audio
    if (currentlyPlaying && currentlyPlaying !== loanAccountNo && audioRefs.current[currentlyPlaying]) {
      audioRefs.current[currentlyPlaying].audioEl.current.pause();
    }
    setCurrentlyPlaying(loanAccountNo);
  };

  // Handle audio ended event
  const handleAudioEnded = (loanAccountNo) => {
    setFullyPlayedRecordings((prev) => ({
      ...prev,
      [loanAccountNo]: true,
    }));
    setCurrentlyPlaying(null);
  };

  // Track audio current time
  const handleTimeUpdate = (loanAccountNo, e) => {
    setAudioCurrentTimes((prev) => ({
      ...prev,
      [loanAccountNo]: e.target.currentTime
    }));
  };

  // Prevent skipping ahead in audio
  const handleSeek = (loanAccountNo, e) => {
    const audioElement = e.target;
    const currentTime = audioCurrentTimes[loanAccountNo] || 0;
    
    // If trying to skip ahead (not backward)
    if (audioElement.currentTime > currentTime + 3) { // Allow small forward movement for buffering
      // Prevent seeking forward
      audioElement.currentTime = currentTime;
      
      // Show toast message
      showToast({
        variant: "warning",
        message: "You need to listen to the full audio recording",
      });
    }
  };

  // Check if all first 10 Poonawalla items are verified via API
  const allVerified = poonawallaData.every(
    (item) => auditStatus[item.loan_account_no] === true || submittedItems[item.key]
  );

  // Trigger final submission
  const triggerSubmission = async () => {
    try {
      const data = await changeCall_AuditStatus({ type: "step_count", step_count: 9 });
      if (data) {
        setAuditCallStepCount(9);
      }
    } catch (error) {
      console.error("Error submitting audit status:", error);
      alert("Submission failed. Please try again.");
    }
  };

  return (
    <>
      <div style={{ padding: "50px" }}>
        <div className="alert alert-primary py-2 d-flex align-item-center mx-2" role="alert">
          <span>
            <FaInfo />
          </span>
          <span>
            Following are the 11 criterias that a tele-collection agent must follow on each call.
            <a
              href="https://drive.google.com/file/d/1SPfmaL0fPRIbSWZyTSg88HfufpJWs8I4/view?usp=drivesdk"
              target="_blank"
              rel="noreferrer"
            >
              {" "}Click here to view
            </a>
          </span>
        </div>

        {poonawallaData.length > 0 ? (
          <>
            <div className="mb-3">
              <small className="text-muted">
                Showing first 10 call recordings ({poonawallaData.length} records)
              </small>
            </div>

            <table className="table table-bordered border table-sm">
              <thead className="thead-light">
                <tr>
                  <th className="p-3 text-center align-top">Call Time</th>
                  <th className="p-3 text-center align-top">Client Name</th>
                  <th className="p-3 text-center align-top">Disposition</th>
                  <th className="p-3 text-center align-top">Take Action</th>
                </tr>
              </thead>
              <tbody>
                {poonawallaData.map((item, index) => (
                  <tr key={item.key || index} style={{ background: index % 2 === 0 ? "transparent" : "#fbfbfb" }}>
                    <td className="p-3 text-center">{item.call_time}</td>
                    <td className="p-3 text-center">{item.client_name}</td>
                    <td className="p-3 text-center">{item.disposition}</td>
                    <td className="p-3 text-center">
                      <TakeActionButton
                        key={item.key}
                        itemId={item.key}
                        loanAccountNo={item.loan_account_no}
                        onSubmit={handleSubmission}
                        isActionTaken={auditStatus[item.loan_account_no] === true}
                        data={item}
                        disposition={item.disposition}
                        isAudioUnlocked={
                          fullyPlayedRecordings[item.loan_account_no] || auditStatus[item.loan_account_no]
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Button 
              onClick={triggerSubmission} 
              variant="primary" 
              className="mt-3" 
              disabled={!allVerified}
            >
              Submit ({poonawallaData.length} records processed)
            </Button>
          </>
        ) : (
          <div className="alert alert-info" role="alert">
            <FaInfo className="me-2" />
            No Poonawalla call recordings found.
          </div>
        )}
      </div>
    </>
  );
};

export default Poonawalla;